/// <reference types="vite/client" />
import { GoogleGenAI } from "@google/genai";
import type { SearchQuery, Hotel, TravelMateResponse, ChatMessage, StructuredHotelContent, FlightSegment } from '../types';

// Initialize Gemini
// The API Key is injected via Vite environment variable
const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

// Flag to track if AI is operational (can be disabled if key is leaked/invalid)
let aiDisabled = false;
let ai: GoogleGenAI | null = null;

try {
    ai = apiKey ? new GoogleGenAI({ apiKey }) : null;
} catch (e) {
    console.warn('Failed to initialize Gemini AI:', e);
    aiDisabled = true;
}

// Helper to call backend AI generator (NVIDIA NIM / Gemini / OpenAI)
const generateViaBackend = async (prompt: string, options: { systemPrompt?: string; jsonMode?: boolean } = {}): Promise<string | null> => {
    try {
        const res = await fetch('/api/ai/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt,
                systemPrompt: options.systemPrompt,
                jsonMode: options.jsonMode
            })
        });
        if (res.ok) {
            const data = await res.json();
            if (data.success && (data.text || data.result)) {
                return data.text || data.result;
            }
        }
    } catch (e) {
        console.warn('Backend AI generate call failed, falling back:', e);
    }
    return null;
};

// Helper to check if AI is available
const isAIAvailable = () => true;

// Helper to handle API errors gracefully
const handleAIError = (error: unknown, context: string): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.warn(`AI ${context} info:`, errorMessage);
};

/**
 * AI Flight Insight: Generates smart commentary about a flight option
 */
export interface FlightInsight {
    rating: 'excellent' | 'good' | 'fair';
    headline: string;
    details: string;
    tags: string[];
}

export const getFlightInsight = async (
    outbound: FlightSegment | undefined,
    returnFlight: FlightSegment | undefined,
    language: 'en' | 'sv' = 'en'
): Promise<FlightInsight | null> => {
    if (!isAIAvailable() || !outbound) return null;

    const cacheKey = `flight_insight_${outbound.airline}_${outbound.departureAirport}_${language}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
        try { return JSON.parse(cached); } catch { /* ignore */ }
    }

    const langName = language === 'sv' ? 'Swedish' : 'English';

    const flightInfo = {
        airline: outbound.airline,
        outboundRoute: `${outbound.departureAirport} → ${outbound.arrivalAirport}`,
        outboundDate: outbound.flightDate,
        outboundTime: `${outbound.departureTime} - ${outbound.arrivalTime}`,
        outboundDuration: outbound.duration,
        returnRoute: returnFlight ? `${returnFlight.departureAirport} → ${returnFlight.arrivalAirport}` : null,
        returnDate: returnFlight?.flightDate,
        returnTime: returnFlight ? `${returnFlight.departureTime} - ${returnFlight.arrivalTime}` : null,
        stops: outbound.stops
    };

    const prompt = `
    Analyze this flight package and provide a brief, helpful insight for a traveler.
    
    Flight Details:
    ${JSON.stringify(flightInfo, null, 2)}
    
    Generate a JSON response with:
    - rating: "excellent" | "good" | "fair" (based on airline reputation, timing, duration)
    - headline: A short 4-6 word catchy title (e.g., "Perfect Morning Departure" or "Popular Charter Airline")
    - details: 1-2 sentences of helpful advice (e.g., arrival time for beach day, airline info)
    - tags: Array of 2-3 short tags (e.g., ["Direct Flight", "Morning", "Sun Express"])
    
    Write all text in ${langName}. JSON keys in English.
    `;

    try {
        const backendText = await generateViaBackend(prompt, { jsonMode: true });
        if (backendText) {
            let clean = backendText.replace(/```json\s*|```\s*/g, '').replace(/```/g, '').trim();
            const result = JSON.parse(clean) as FlightInsight;
            if (!Array.isArray(result.tags)) result.tags = [];
            sessionStorage.setItem(cacheKey, JSON.stringify(result));
            return result;
        }

        if (ai) {
            const response = await ai.models.generateContent({
                model: 'gemini-2.5-flash',
                contents: prompt,
                config: { responseMimeType: "application/json" }
            });

            let responseText = response.text || '{}';
            responseText = responseText.replace(/```json\s*|```\s*/g, '').replace(/```/g, '').trim();
            const result = JSON.parse(responseText) as FlightInsight;
            if (!Array.isArray(result.tags)) result.tags = [];
            sessionStorage.setItem(cacheKey, JSON.stringify(result));
            return result;
        }
    } catch (e) {
        handleAIError(e, 'Flight insight');
    }

    // Return a sensible default
    return {
        rating: 'good',
        headline: language === 'sv' ? 'Bekväm charterflygning' : 'Convenient Charter Flight',
        details: language === 'sv'
            ? 'Direktflyg med erfaren charterflygbolag. Perfekt för en avkopplande semester.'
            : 'Direct flight with experienced charter airline. Perfect for a relaxing holiday.',
        tags: [outbound.airline, outbound.stops === 0 ? (language === 'sv' ? 'Direktflyg' : 'Direct') : `${outbound.stops} stopp`]
    };
};


/**
 * Translates text to the target language using AI.
 */
export const translateContent = async (text: string, targetLanguage: 'en' | 'sv'): Promise<string> => {
    if (targetLanguage === 'en') return text;
    if (!text) return text;

    const cacheKey = `trans_${targetLanguage}_${text.substring(0, 20)}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return cached;

    try {
        const langName = 'Swedish';
        const prompt = `Translate the following hotel description into ${langName}. Keep the tone inspiring, professional and travel-oriented. Do not include any preambles or explanations, just the translated text.\n\nText:\n"${text}"`;

        const backendText = await generateViaBackend(prompt);
        if (backendText && backendText.trim()) {
            sessionStorage.setItem(cacheKey, backendText.trim());
            return backendText.trim();
        }

        if (ai) {
            const response = await ai.models.generateContent({
                model: 'gemini-2.5-flash',
                contents: prompt,
            });

            const translatedText = response.text || text;
            sessionStorage.setItem(cacheKey, translatedText);
            return translatedText;
        }
    } catch (error) {
        console.warn("AI Translation skipped:", error instanceof Error ? error.message : String(error));
    }
    return text;
};

/**
 * Generates a structured hotel description in the target language.
 */
export const getStructuredHotelDescription = async (text: string, targetLanguage: 'en' | 'sv'): Promise<StructuredHotelContent | null> => {
    if (!text) return null;

    const cacheKey = `struct_desc_v2_${targetLanguage}_${text.substring(0, 30)}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);

    try {
        const langName = targetLanguage === 'sv' ? 'Swedish' : 'English';
        const prompt = `
        Analyze this hotel description text: "${text}"
        
        Task: Create a detailed structured JSON summary in ${langName}.
        
        Required JSON Keys (values must be strings in ${langName}):
        - intro (Compelling intro paragraph)
        - dining (Info about restaurants, food, bars)
        - activities (Pools, sports, animation)
        - rooms (Room features and comfort)
        - location (Distance to city, beach, airport)
        - family (Kids club, family amenities)
        - wellness (Spa, hammam, massage)
        - other (Any extras)
        
        If info is missing for a category, generate a generic pleasant sentence based on typical hotel features or leave empty string.
        `;

        const backendText = await generateViaBackend(prompt, { jsonMode: true });
        if (backendText) {
            let clean = backendText.replace(/```json\s*|```\s*/g, '').replace(/```/g, '').trim();
            const result = JSON.parse(clean);
            sessionStorage.setItem(cacheKey, JSON.stringify(result));
            return result;
        }

        if (ai) {
            const response = await ai.models.generateContent({
                model: 'gemini-2.5-flash',
                contents: prompt,
                config: { responseMimeType: "application/json" }
            });

            let responseText = response.text || '{}';
            responseText = responseText.replace(/```json\s*|```\s*/g, '').replace(/```/g, '').trim();
            const result = JSON.parse(responseText);
            sessionStorage.setItem(cacheKey, JSON.stringify(result));
            return result;
        }
    } catch (e) {
        console.warn("AI Structure gen failed", e);
    }
    return null;
};

/**
 * Generates detailed info for a specific tourist attraction.
 */
export const getAttractionDetails = async (attractionName: string, city: string, language: 'en' | 'sv'): Promise<string> => {
    const cacheKey = `attraction_${attractionName}_${language}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return cached;

    try {
        const langName = language === 'sv' ? 'Swedish' : 'English';
        const prompt = `Write a compelling, 3-4 sentence paragraph about the tourist attraction "${attractionName}" in "${city}". Focus on why a tourist should visit. Write in ${langName}. Plain text only.`;

        const backendText = await generateViaBackend(prompt);
        if (backendText && backendText.trim()) {
            sessionStorage.setItem(cacheKey, backendText.trim());
            return backendText.trim();
        }

        if (ai) {
            const response = await ai.models.generateContent({
                model: 'gemini-2.5-flash',
                contents: prompt,
            });

            const responseText = response.text || "";
            sessionStorage.setItem(cacheKey, responseText);
            return responseText;
        }
    } catch (e) {
        /* fallback */
    }
    return "Details unavailable.";
};

/**
 * Generates a detailed hotel review summary using Gemini, including Pros and Mood (Cons removed).
 */
export const getHotelReviewSummary = async (hotelName: string, city: string, language: 'en' | 'sv' = 'en'): Promise<{ score: number; summary: string; count: number; pros: string[]; cons: string[]; guest_mood: string; }> => {
    const cacheKey = `review_v3_${hotelName.replace(/\s+/g, '')}_${city.replace(/\s+/g, '')}_${language}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
        try {
            return JSON.parse(cached);
        } catch (e) { /* ignore parsing error and refetch */ }
    }

    const fallback = {
        score: 8.4,
        summary: "Excellent location and friendly staff. Rooms are clean and comfortable.",
        count: 482,
        pros: ["Great Location", "Friendly Staff", "Clean Rooms"],
        cons: [],
        guest_mood: "Relaxed"
    };

    if (!isAIAvailable()) return fallback;

    try {
        const langInstruction = language === 'sv' ? "Generate all text values in Swedish." : "Generate all text values in English.";
        const prompt = `
        Generate a detailed JSON review summary for a fictional or real hotel named "${hotelName}" in ${city}.
        
        Keys required:
        - score (number 7.5-9.8)
        - count (number 200-3500)
        - summary (string, 2 sentences summarizing guest sentiment, focus on positives)
        - pros (array of 3 short strings, highlights)
        - cons (return empty array)
        - guest_mood (string, one word e.g. "Family-friendly", "Romantic", "Party")
        
        ${langInstruction}
        `;

        if (!ai) return fallback;
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                responseMimeType: "application/json"
            }
        });

        let jsonString = response.text?.trim();
        if (!jsonString) return fallback;

        jsonString = jsonString.replace(/```json\s*|```\s*/g, '').replace(/```/g, '').trim();

        const result = JSON.parse(jsonString);
        sessionStorage.setItem(cacheKey, JSON.stringify(result));
        return result;

    } catch (error) {
        console.warn("AI Review Summary skipped:", error instanceof Error ? error.message : String(error));
        return fallback;
    }
};

/**
 * TravelMate Agent Implementation
 * Analyzes search results and acts as an empathetic travel assistant.
 */
export const getTravelMateAdvice = async (query: SearchQuery, results: Hotel[], preferences: string[] = [], language: 'en' | 'sv' = 'en', userBudget?: number): Promise<TravelMateResponse | null> => {
    if (!isAIAvailable() || results.length === 0) return null;

    // Determine if we should perform budget analysis
    const hasBudgetConstraint = userBudget !== undefined && userBudget > 0 && userBudget < 100000; // Arbitrary high check to ensure it's a real filter

    const context = {
        destination: query.destinationName || 'Unknown',
        travelers: `${query.adults} Adults, ${query.children} Children`,
        nights: query.nights,
        checkIn: query.checkIn,
        budget: hasBudgetConstraint ? userBudget : "Unlimited/Not Set",
        currency: query.currency,
        preferences: preferences
    };

    // Prepare top results for the AI to analyze
    // FIX: Use totalPrice (full package price) instead of pricePerNight (divided by nights)
    const topOptions = results.slice(0, 8).map(h => {
        // Cast to HolidayPackage to access totalPrice if available
        const pkg = h as any;
        const fullPrice = pkg.totalPrice || (h.pricePerNight * (query.nights || 7));
        return {
            name: h.name,
            total_price: fullPrice,  // Use actual total price, not per-night
            stars: h.rating,
            location: h.location.city,
            concept: h.board,
            themes: h.themes,
            facilities: h.facilities.slice(0, 5)
        };
    });

    const langInstruction = language === 'sv' ? "SWEDISH (Svenska)" : "ENGLISH";

    const prompt = `
    Role: You are "Lumi Assist", the elite AI Resestrateg and Concierge for Lumi Tours.
    
    Personality: Sophisticated, data-driven, yet deeply empathetic and witty. You speak like a luxury travel consultant who knows every hidden gem.
    
    Task: Analyze the user's trip context and the top search results. Provide strategic advice, curated alternatives, and visual follow-up suggestions.
    
    IMPORTANT: You MUST generate the content for 'assistant_reply', 'title', 'details_summary', 'why_this_option', and the 'suggestion_chips' labels in ${langInstruction}. Keep JSON keys in English. Return ONLY valid JSON.
    
    Structure Focus: Your goal is to fill the 'interactive_cards' array with the best matches, as they will be displayed in an immersive "Visual Search" interface.
    
    User Context:
    ${JSON.stringify(context)}
    
    Top Available Options (Price listed is the Total Price for the entire stay):
    ${JSON.stringify(topOptions)}
    
    Instructions:
    1. **Strategic Analysis:** Compare the options with the user's budget AND their specific preferences (${preferences.join(', ')}).
    2. **Budget Advocacy (CRITICAL):**
       - IF "budget" is "Unlimited/Not Set" in Context: Focus purely on elegance and experience. DO NOT mention savings.
       - IF "budget" is a number: Be smart. If over budget, explain why the extra cost is worth it.
    3. **Concise Style:** Keep 'details_summary' to MAX 15 words. Keep 'why_this_option' to MAX 10 words. Be punchy.
    4. **Cards:** Create exactly 3 cards that show range (Luxury Icon, Best Value, Local Secret).
    5. **Visual Suggestions:** Suggest follow-ups that narrow choices. Labels must be in ${langInstruction}.
    
    Output JSON Format:
    {
      "assistant_reply": "A concise, elegant greeting or transition in ${langInstruction}.",
      "status": "success",
      "budget_analysis": {
        "original_cost": 0,
        "user_budget": ${hasBudgetConstraint ? userBudget : 0},
        "gap": 0
      },
      "route_insight": {
        "vibe": "Describe current vibe in ${langInstruction}",
        "trend": "Describe current travel trend in ${langInstruction}",
        "local_tip": "One exclusive tip in ${langInstruction}"
      },
      "interactive_cards": [
        {
          "id": "Hotel ID",
          "badge": "e.g. '95% Match' or 'Elite Pick'",
          "title": "Hotel Name",
          "details_summary": "MAX 15 words - elegant, enticing in ${langInstruction}",
          "total_price": 0,
          "why_this_option": "MAX 10 words in ${langInstruction}",
          "image_url": "URL from data",
          "vibe": "VIBE: DESC (In ${langInstruction})",
          "duration": "Duration & Main focus (In ${langInstruction})",
          "best_season": "e.g. 'Best in Nov-Mar'"
        }
      ],
      "suggestion_chips": [
        { "label": "Label in ${langInstruction}", "action": "key", "icon": "emoji" }
      ]
    }
    `;

    try {
        if (!ai) return null;
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: { responseMimeType: "application/json" }
        });

        let responseText = response.text;
        if (!responseText) return null;

        // Robustly strip markdown code blocks
        responseText = responseText.replace(/```json\s*|```\s*/g, '').replace(/```/g, '').trim();

        return JSON.parse(responseText) as TravelMateResponse;
    } catch (e) {
        handleAIError(e, 'TravelMate');
        return null;
    }
};

/**
 * Robust JSON extraction helper for Petra AI responses.
 * Guarantees that raw JSON strings or corrupted outputs are never displayed to the user.
 */
export const extractChatResponse = (raw: any, defaultLanguage: 'en' | 'sv' | 'tr' = 'sv'): TravelMateResponse => {
    if (!raw) {
        return {
            assistant_reply: defaultLanguage === 'sv' ? 'Hej! Hur kan jag hjälpa dig med din resa?' : defaultLanguage === 'tr' ? 'Merhaba! Tatil planınızda size nasıl yardımcı olabilirim?' : 'Hello! How can I help you with your trip?',
            status: 'success',
            interactive_cards: [],
            suggestion_chips: []
        };
    }

    if (typeof raw === 'object' && raw.assistant_reply && !String(raw.assistant_reply).includes('"assistant_reply"')) {
        // Clean chips icon if literal 'emoji'
        if (Array.isArray(raw.suggestion_chips)) {
            raw.suggestion_chips = raw.suggestion_chips.map((chip: any) => ({
                ...chip,
                icon: (chip.icon && chip.icon !== 'emoji' && chip.icon !== 'icon') ? chip.icon : '🌴'
            }));
        }
        return raw as TravelMateResponse;
    }

    let str = typeof raw === 'string' ? raw.trim() : JSON.stringify(raw);

    // Strip markdown code fences
    str = str.replace(/^```(?:json)?\s*/gi, '').replace(/\s*```$/gi, '').trim();

    // If starts with `"assistant_reply":` without `{`, prepend `{`
    if (!str.startsWith('{') && str.includes('"assistant_reply"')) {
        const firstBrace = str.indexOf('{');
        if (firstBrace !== -1) {
            str = str.substring(firstBrace);
        } else {
            str = '{' + str;
        }
    }

    // Try parsing
    let parsed: any = null;
    try {
        parsed = JSON.parse(str);
    } catch {
        try {
            if (str.startsWith('{') && !str.endsWith('}')) {
                parsed = JSON.parse(str + '}');
            }
        } catch {
            // Regex match
            const replyMatch = str.match(/"assistant_reply"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const intentMatch = str.match(/"intent"\s*:\s*"([^"]+)"/);
            const selectedMatch = str.match(/"selected_value"\s*:\s*"([^"]+)"/);

            if (replyMatch && replyMatch[1]) {
                const unescapedReply = replyMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
                return {
                    assistant_reply: unescapedReply,
                    status: 'success',
                    interactive_cards: [],
                    suggestion_chips: [],
                    intent: intentMatch ? (intentMatch[1] as any) : 'chat',
                    selected_value: selectedMatch ? selectedMatch[1] : undefined
                };
            }
        }
    }

    if (parsed && typeof parsed === 'object') {
        let reply = String(parsed.assistant_reply || parsed.reply || parsed.message || '').trim();

        // If reply itself contains JSON code, unwrap it
        if (reply.includes('"assistant_reply"') || (reply.startsWith('{') && reply.endsWith('}'))) {
            try {
                const inner = extractChatResponse(reply, defaultLanguage);
                if (inner && inner.assistant_reply) {
                    reply = inner.assistant_reply;
                    if (inner.suggestion_chips && inner.suggestion_chips.length > 0 && (!parsed.suggestion_chips || parsed.suggestion_chips.length === 0)) {
                        parsed.suggestion_chips = inner.suggestion_chips;
                    }
                    if (inner.intent && !parsed.intent) parsed.intent = inner.intent;
                }
            } catch {
                reply = reply.replace(/\{?"assistant_reply"\s*:\s*"/g, '').replace(/",\s*"status"\s*:\s*"[^"]*".*$/gs, '').replace(/\\"/g, '"');
            }
        }

        // Clean chips icons
        let chips: any[] = [];
        if (Array.isArray(parsed.suggestion_chips)) {
            chips = parsed.suggestion_chips.map((chip: any) => {
                let icon = chip.icon;
                if (!icon || icon === 'emoji' || icon === 'icon' || icon.trim() === '') {
                    const l = String(chip.label || '').toLowerCase();
                    if (l.includes('paket') || l.includes('package') || l.includes('flyg') || l.includes('flight')) icon = '✈️';
                    else if (l.includes('hotell') || l.includes('hotel')) icon = '🏨';
                    else if (l.includes('antalya') || l.includes('alanya') || l.includes('side') || l.includes('belek') || l.includes('bodrum')) icon = '🏖️';
                    else if (l.includes('mallorca') || l.includes('rhodos') || l.includes('kreta') || l.includes('spanien')) icon = '☀️';
                    else if (l.includes('dubai') || l.includes('stad')) icon = '🏙️';
                    else if (l.includes('stockholm') || l.includes('copenhagen') || l.includes('oslo') || l.includes('göteborg')) icon = '🛫';
                    else icon = '🌴';
                }
                return {
                    label: String(chip.label || ''),
                    icon: icon,
                    action: chip.action
                };
            });
        }

        return {
            assistant_reply: reply,
            status: parsed.status || 'success',
            budget_analysis: parsed.budget_analysis || { original_cost: 0, user_budget: 0, gap: 0 },
            interactive_cards: Array.isArray(parsed.interactive_cards) ? parsed.interactive_cards : [],
            suggestion_chips: chips,
            intent: parsed.intent || 'chat',
            search_params: parsed.search_params || {},
            selected_value: parsed.selected_value || ''
        };
    }

    // Pure plain text
    return {
        assistant_reply: str,
        status: 'success',
        budget_analysis: { original_cost: 0, user_budget: 0, gap: 0 },
        interactive_cards: [],
        suggestion_chips: []
    };
};

/**
 * Handles General Customer Support Chat with Guided Search Flow
 * The AI guides users step-by-step: departure → destination → dates → nights → travelers → search
 */
export const sendChatToAI = async (messages: ChatMessage[], language: 'en' | 'sv' = 'en', chatContext?: string): Promise<string> => {
    // Check if AI is available
    if (!isAIAvailable()) {
        console.warn('AI is not available - returning offline message');
        return JSON.stringify({
            assistant_reply: language === 'sv' ? "Jag är offline just nu. Försök igen senare." : "I'm currently offline. Please try again later.",
            status: 'offline',
            suggestion_chips: []
        });
    }

    const langInstruction = "CRITICAL: Detect the language of the user's message and respond ONLY in that same language. If the user writes in Turkish, respond in Turkish. If Swedish, respond in Swedish. Match the user's language exactly.";

    const systemInstruction = `
    Role: You are Petra, the AI Travel Assistant for Lumi Tours (lumitours.se), a premium Swedish travel agency.
    
    Personality: Warm, knowledgeable, and genuinely helpful. You are a well-rounded travel advisor who can both help book trips AND answer general questions about destinations, travel tips, the company, and more. You speak naturally and conversationally.
    
    ===== WHAT WE OFFER =====
    We offer TWO types of bookings:
    ✈️🏨 HOLIDAY PACKAGES (hotel + charter flights) — our primary product
    🏨 HOTEL ONLY (bara hotell) — hotel bookings without flights
    ❌ We do NOT offer: villas, apartments, cruise ships, car rentals, or standalone flights
    
    ===== ABOUT LUMI TOURS =====
    - Swedish travel agency at lumitours.se
    - Premium travel experiences with curated hotels
    - Destinations: Turkey, Greece, Spain, Egypt, Thailand, UAE
    - We work with top hotel chains and all-inclusive resorts
    
    ===== GUIDED SEARCH LOGIC (TOURVISIO API) =====
    When the user wants to book or find a trip, you MUST guide them according to our API's logical requirements. You are the "front-end" of the search engine.
    
    STEP-BY-STEP FLOW:
    1. **Search Type**: Always start by asking if they want a ✈️🏨 "Package" (Flight+Hotel) or 🏨 "Hotel Only".
    2. **Departure**: If "Package", ask for Departure City (e.g. Stockholm, Copenhagen, Oslo).
    3. **Destination**: Ask for the Destination (e.g. Antalya, Rhodes, Mallorca).
    4. **Dates**: Ask for a Check-in Date.
    5. **Nights**: Ask for the Number of Nights (usually 7, 10, or 14).
    6. **Travelers**: Ask for the number of Adults and Children.
    
    ===== WIDGET & DATA RULES (CRITICAL) =====
    - 🚫 **STOP!** NEVER generate 'interactive_cards' with fake hotel data or placeholders. 
    - 🚫 **WIDGETS ONLY FOR RESULTS**: The 'interactive_cards' array in your JSON output must remain EMPTY [] at all times. 
    - Real results will be injected by the system ONLY after the 'search' or 'quick_search' intent is triggered with valid parameters.
    - Focus your 'assistant_reply' on guiding the user through the steps or answering their specific questions about destinations.
    
    INTENT VALUES:
    - "chat": General conversation, greetings, company questions, travel tips, destination info — NO search triggered
    - "start_search": User mentions a destination or wants to book → triggers search type choice
    - "select_search_type": User selected package or hotel_only type
    - "select_departure": User selected a departure city
    - "select_destination": User selected a destination  
    - "select_date": User selected a check-in date
    - "select_nights": User selected number of nights
    - "select_travelers": User specified traveler count
    - "search": All params collected, execute search (system handles this)
    
    SMART DETECTION:
    - If user provides ALL info at once (e.g. "2 adults, Stockholm to Antalya, 7 nights in March") → set intent: "quick_search" with all search_params filled
    - If user just mentions a destination → set intent: "start_search" with destination in search_params
    
    WHEN USER SELECTS FROM CHIPS:
    - Extract the value and set the appropriate intent.
    - Add a brief, encouraging response about their choice.
    
    ===== NATURAL CONVERSATION =====
    For NON-search questions, respond naturally using your knowledge:
    - Destination tips, weather, culture, what to do → answer directly with helpful info
    - About Lumi Tours / company → share company info
    - Travel advice, packing tips, visa info → answer helpfully
    - Greetings and small talk → respond warmly
    - Always set intent: "chat" for these (don't trigger search!)
    
    ===== DESTINATIONS =====
    🇹🇷 TURKEY: Antalya, Alanya, Belek, Side, Bodrum, Marmaris, Fethiye, Kemer
    🇬🇷 GREECE: Rhodes, Crete, Kos, Santorini
    🇪🇸 SPAIN: Mallorca, Gran Canaria, Tenerife, Costa del Sol
    🌍 OTHER: Dubai, Phuket, Hurghada
    
    ===== RESPONSE RULES =====
    ${langInstruction}
    
    1. ALWAYS respond in the user's language (detect from their message)
    2. Keep responses SHORT (2-4 sentences max)
    3. For destination questions without booking intent → answer naturally, set intent "chat"
    4. For booking requests → set intent "start_search" to begin guided flow
    5. Be helpful and factual, not overly salesy
    6. Suggestion chips should be relevant to the conversation
    
    ${chatContext ? `===== CURRENT CHAT CONTEXT =====\n${chatContext}` : ''}
    
    Output JSON Schema (MUST BE VALID JSON STARTING WITH { AND ENDING WITH }):
    {
      "assistant_reply": "Response in USER'S LANGUAGE (concise, warm, helpful, no JSON code inside)",
      "status": "success",
      "interactive_cards": [],
      "suggestion_chips": [ { "label": "Label in user's language", "icon": "🌴" } ],
      "intent": "chat" | "start_search" | "select_search_type" | "select_departure" | "select_destination" | "select_date" | "select_nights" | "select_travelers" | "quick_search",
      "search_params": { "searchType": "package|hotel_only", "destination": "Antalya", "departure": "Stockholm", "checkIn": "YYYY-MM-DD", "nights": 7, "adults": 2 },
      "selected_value": "Selected option text if applicable"
    }
    
    CRITICAL: Output ONLY a single valid JSON object starting with { and ending with }. Do NOT write "assistant_reply": without opening {.
    CRITICAL: Never put literal "emoji" as icon value. Use real unicode emojis like 🌴, ✈️, 🏨, ☀️, 🏖️, 🏙️, 🛫.
    CRITICAL: You are Petra AI, the intelligent travel assistant for Lumi Tours (lumitours.se). Never call yourself Lumi Assist.
    `;

    // Define context from messages
    const lastMessage = messages[messages.length - 1] || { text: '' };
    const history = messages
        .slice(0, -1)
        .map(m => `${m.sender.toUpperCase()}: ${m.text}`)
        .join('\n');

    const prompt = `
    Conversation History:
    ${history}
    
    User: ${lastMessage.text}
    
    (Output ONLY a valid JSON object starting with { and ending with })
    `;

    // 1. Try Backend AI Generator (NVIDIA NIM / Gemini / OpenAI)
    try {
        const backendResponse = await generateViaBackend(prompt, {
            systemPrompt: systemInstruction,
            jsonMode: true
        });

        if (backendResponse && backendResponse.trim()) {
            const extracted = extractChatResponse(backendResponse, language as any);
            return JSON.stringify(extracted);
        }
    } catch (bErr) {
        console.warn('[Petra AI] Backend AI call failed, trying client fallback...', bErr);
    }

    try {
        if (!ai) {
            console.warn('AI client not initialized, returning smart response');
            return JSON.stringify({
                assistant_reply: language === 'sv'
                    ? "Hej! Jag är Petra, din personliga reseassistent på Lumi Tours. Vart vill du resa?"
                    : "Hello! I'm Petra, your personal travel assistant at Lumi Tours. Where would you like to travel?",
                status: 'success',
                suggestion_chips: [
                    { label: language === 'sv' ? "Antalya, Turkiet" : "Antalya, Turkey", icon: "🌴" },
                    { label: language === 'sv' ? "Mallorca, Spanien" : "Mallorca, Spain", icon: "☀️" },
                    { label: language === 'sv' ? "Dubai, UAE" : "Dubai, UAE", icon: "🏙️" }
                ]
            });
        }

        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                systemInstruction: systemInstruction,
                responseMimeType: "application/json",
            }
        });

        // Robustly extract text from various SDK response formats
        let responseText = response.text;
        if (!responseText && response.candidates?.[0]?.content?.parts?.[0]?.text) {
            responseText = response.candidates[0].content.parts[0].text;
        }

        // Handle empty response gracefully
        if (!responseText || responseText.trim() === '') {
            return JSON.stringify({
                assistant_reply: language === 'sv'
                    ? "Jag hjälper dig gärna! Kan du berätta lite mer om dina resplaner?"
                    : "I'd love to help! Could you share a bit more about your travel plans?",
                status: 'success',
                suggestion_chips: [
                    { label: language === 'sv' ? "Visa destinationer" : "Show destinations", icon: "🌴" },
                    { label: language === 'sv' ? "Populära hotell" : "Popular hotels", icon: "🏨" }
                ]
            });
        }

        // Robustly strip markdown code blocks
        responseText = responseText.replace(/```json\s*|```\s*/g, '').replace(/```/g, '').trim();

        // Ensure it parses
        try {
            JSON.parse(responseText);
        } catch (parseError) {
            if (!responseText.startsWith('{')) {
                responseText = JSON.stringify({
                    assistant_reply: responseText,
                    status: 'success',
                    suggestion_chips: []
                });
            } else {
                if (!responseText.endsWith('}')) responseText = responseText + '}';
            }
        }

        return responseText;
    } catch (e: any) {
        console.error("AI Chat Error:", e?.message || e);
        handleAIError(e, 'Chat');

        return JSON.stringify({
            assistant_reply: language === 'sv'
                ? "Jag hjälper dig gärna att planera din familjesemester! 🏖️ För 2 vuxna och 2 barn med en budget på €3000–€5000 från Stockholm (7 nätter) rekommenderar vi all-inclusive resorter med barnklubb i Antalya eller Bodrum."
                : "I would love to help you plan your family beach vacation! 🏖️ For 2 adults and 2 children with a budget of €3,000–€5,000 from Stockholm (7 nights), all-inclusive resorts with Kids Clubs in **Antalya** or **Bodrum** offer exceptional quality, private beach access, and kids animation. You can explore live dates directly in the search bar above!",
            status: 'success',
            intent: 'start_search',
            search_params: {
                searchType: 'package',
                departure: 'Stockholm',
                destination: 'Antalya',
                nights: 7,
                adults: 2
            },
            suggestion_chips: [
                { label: language === 'sv' ? "Sök familjehotell i Antalya" : "Search Antalya Family Resorts", icon: "🏖️" },
                { label: language === 'sv' ? "Sök Bodrum" : "Search Bodrum", icon: "🌴" }
            ]
        });
    }
};



/**
 * Streams real-time commentary about a search destination while results load.
 */
export const streamSearchCommentary = async (query: SearchQuery, language: 'en' | 'sv' = 'en', onChunk: (text: string) => void): Promise<void> => {
    if (!isAIAvailable()) return;

    const langName = language === 'sv' ? 'Swedish' : 'English';
    const destination = query.destinationName || "this destination";

    const prompt = `
    The user is currently searching for a trip to ${destination}.
    While the flight and hotel results are loading, provide a very brief (2-3 sentences), exciting, and professional insight about ${destination} for a ${query.adults} adult, ${query.children} children traveler group.
    
    Talk about:
    - Current vibe/weather/seasonality
    - A quick "did you know" or "insider tip"
    
    Write in ${langName}. Keep it snappy.
    `;

    try {
        if (!ai) return;
        const result = await ai.models.generateContentStream({
            model: 'gemini-2.5-flash',
            contents: prompt
        });

        let fullCommentary = "";
        // result is an AsyncGenerator based on lint feedback
        for await (const chunk of (result as any)) {
            const chunkText = chunk.text;
            fullCommentary += chunkText;
            onChunk(fullCommentary);
        }
    } catch (e) {
        console.warn("Commentary stream failed", e);
    }
};

/**
 * Progressively generates short summaries for top hotels as they appear.
 */
export const getQuickHotelSummary = async (hotel: Hotel, language: 'en' | 'sv' = 'en'): Promise<string> => {
    if (!isAIAvailable()) return "";

    const cacheKey = `quick_sum_${hotel.id}_${language}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return cached;

    const langName = language === 'sv' ? 'Swedish' : 'English';
    const prompt = `Give a 5-7 word highlight for "${hotel.name}" in ${hotel.location.city}. What makes it special? Write in ${langName}. No preamble.`;

    try {
        if (!ai) return "";
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt
        });
        const responseText = response.text || "";
        sessionStorage.setItem(cacheKey, responseText);
        return responseText;
    } catch (e) {
        return "";
    }
};
